// @flow

import { useEffect, useRef } from 'react';
import { useTenant } from '@tectonics/commons/tenant';
import { useEnrollment } from '../context';
import { pollEnrollmentStatus } from '../services/enrollment-api';
import type { RegistrationOptions } from '../services/webauthn';

const POLLING_INTERVAL_MS = 5000;
const POLLING_TIMEOUT_MS = 300000;
const TERMINAL_ERROR_STATUSES = ['revoked', 'cancelled', 'not_accepted', 'expired'];

function isTerminalErrorStatus(status: ?string): boolean {
  return typeof status === 'string' && TERMINAL_ERROR_STATUSES.includes(status);
}

export function useEnrollmentPolling() {
  const tenant = useTenant();
  const { data, dispatch } = useEnrollment();
  const pollingStartRef = useRef<?number>(null);
  const timerRef = useRef<?TimeoutID>(null);

  useEffect(() => {
    if (data.state !== 'POLLING') {
      return;
    }

    let cancelled = false;
    pollingStartRef.current = Date.now();

    async function poll() {
      if (cancelled) return;

      const startTime = pollingStartRef.current;
      if (startTime && Date.now() - startTime > POLLING_TIMEOUT_MS) {
        if (!cancelled) {
          dispatch({ type: 'SET_ERROR', errorMessage: 'Tempo limite atingido. Tente novamente.' });
        }
        return;
      }

      try {
        const result = await pollEnrollmentStatus(
          data.contract,
          tenant.code,
        );

        if (cancelled) return;

        if (handleResult(result)) return;
      } catch (_e) {
        if (cancelled) return;
      }

      if (!cancelled) {
        timerRef.current = setTimeout(poll, POLLING_INTERVAL_MS);
      }
    }

    function handleResult(result: { +enrollment?: { +status?: string, +registration_options?: RegistrationOptions, +intent_url?: string } }): boolean {
      const enrollment = result.enrollment;
      if (!enrollment) return false;

      if (enrollment.status === 'accepted') {
        dispatch({ type: 'ENROLLMENT_SUCCESS', intentUrl: enrollment.intent_url || '' });
        return true;
      }
      if (isTerminalErrorStatus(enrollment.status)) {
        dispatch({ type: 'SET_TERMINAL_ERROR', errorMessage: 'Ocorreu um erro com a vinculação.', intentUrl: enrollment.intent_url || '' });
        return true;
      }
      if (enrollment.registration_options) {
        dispatch({
          type: 'REGISTRATION_OPTIONS_RECEIVED',
          registrationOptions: enrollment.registration_options,
          intentUrl: enrollment.intent_url || '',
        });
        return true;
      }
      return false;
    }

    poll();

    return () => {
      cancelled = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      pollingStartRef.current = null;
    };
  }, [data.state, data.contract, tenant.code, dispatch]);
}
